import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../redux/slices/currentuserslice";
import {
  setSideBarVisible,
  setSideBarDetailVisible,
} from "../redux/slices/appslice";
import {
  setCurrentTheme,
  setProcessing,
  setUserTheme,
} from "../redux/slices/currentthemeslice";
import { setCurrentUser } from "../redux/slices/currentuserslice";
import { toggleService } from "../redux/slices/dictaphoneslice";
import { addNotification } from "../redux/slices/notificationsslice";
import { hideAppDialog, setAppDialog } from "../redux/slices/appdialogslice";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { api, apiErrorsHandler, getErrorMessages } from "../api/httpclient";
import { Button, DropDownButton } from "@progress/kendo-react-buttons";
import { Icon, SvgIcon } from "@progress/kendo-react-common";
import { notificationDurationMiddle, beepFile } from "../lib/utils";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { ThemesSelector } from "./themesselector";
import { Dialog } from "@progress/kendo-react-dialogs";
import { ChangePasswordForm } from "./changepasswordform";
import { themes } from "../sass/themes";

import "../styles/currentuser.css";
import { setAppearance } from "../redux/slices/appearanceslice";
import {
  bellIcon,
  documentManagerIcon,
  userIcon,
  headsetIcon,
} from "@progress/kendo-svg-icons";
import { setCounter } from "../redux/slices/notificationscounterslice";
import { NOTIFICATIONS_UPDATE_COUNT_IN_MILLISECONDS } from "../settings";
import { useEffectOnce } from "../hooks/useeffectonce";
import { Badge } from "@progress/kendo-react-indicators";

import useInjectLiveChat from "../liveChat";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { canAccessUsers } from "../routing/authorizationhandlers";

//display the user drop down items depending on whether he is a subscription root user or not
const getUserItems = (currentUser, currentTheme, dictaphone, t) => {
  const vasAIIcon =
    dictaphone.status == 0 ? "radiobutton" : "radiobutton-checked";
  let items = [
    {
      id: "theming",
      text: t("currentuser.appearance"),
      icon: "accessibility",
    },
    {
      id: "separator1",
      text: "",
    },
    {
      id: "changePassword",
      text: t("currentuser.changepass"),
      icon: "unlock",
    },
    {
      id: "separator2",
      text: "",
    },
  ];

  // if (currentUser.Subscription.Extras?.ViewConsumption || currentUser.Subscription.Extras?.ManageConsumption)
  // {
  //   items.push( {
  //     id:"dataConsumption",
  //     text: "Data Consumption",
  //     icon: "data"
  //   });

  //   items.push({
  //     id:"separator3",
  //     text: ""
  //   });
  // }

  if (currentUser.Subscription?.IsRoot) {
    items.push({
      id: "reportsMenu",
      text: t("currentuser.sidebar"),
      icon: "cells-merge-vertically",
    });

    items.push({
      id: "separator4",
      text: "",
    });
  }

  items.push({
    id: "logOut",
    text: t("currentuser.logout"),
    icon: "logout",
  });

  return items;
};

const getUserManagementItems = (currentUser, dictaphone, t) => {
  const vasAIIcon =
    dictaphone.status == 0 ? "radiobutton" : "radiobutton-checked";

  let items = [];
  if (canAccessUsers(currentUser)) {
    items.push({
      id: "users",
      text: t("currentuser.users"),
      icon: "user",
    });
    items.push({ id: "separator11", text: "" });
    items.push({
      id: "groups",
      text: t("currentuser.groups"),
      icon: "group",
      canAccess: canAccessUsers(currentUser),
    });
    items.push({
      id: "separator22",
      text: "",
    });
  }
  if (currentUser.Extras?.ViewConsumption) {
    items.push({
      id: "consumption",
      text: t("currentuser.consumption"),
      icon: "data",
    });
    items.push({
      id: "separator33",
      text: "",
    });
  }
  items.push({
    id: "support",
    text: t("header.help"),
    icon: "headset",
  });
  items.push({
    id: "separator33",
    text: "",
  });

  items.push({
    id: "schedule",
    text: t("header.schedule"),
    icon: "clock",
  });
  items.push({
    id: "separator33",
    text: "",
  });

  items.push({
    id: "connection",
    text: t("header.connection"),
    icon: "connector",
  });
  items.push({
    id: "separator33",
    text: "",
  });

  return items;
};
//render every item in user menu
const itemRender = (props) => {
  if (!props.item.text)
    //separator
    return (
      <div className="menu-separator" onClick={(e) => e.preventDefault()} />
    );
  else if (props.item.id == "support")
    return (
      <div className="sub-menu-link">
        <SvgIcon icon={headsetIcon} className={`k-icon k-color-primary`} />
        {`${props.item.text}`}
      </div>
    );
  else
    return (
      <div className="sub-menu-link">
        <span
          id={`userItem-${props.item.id}`}
          className={`k-icon k-i-${props.item.icon} k-color-primary`}
          role="presentation"
        />
        {`${props.item.text}`}
      </div>
    );
};

export const CurrentUser = (props) => {
  const dispatch = useDispatch();
  const app = useSelector((state) => state.app);
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const currentUser = useSelector((state) => state.currentUser);
  const onBoarding = useSelector((state) => state.onBoardingPage);
  const _currentUser = useRef(null);
  _currentUser.current = currentUser;
  const dictaphone = useSelector((state) => state.dictaphone);
  const { switcher, status } = useThemeSwitcher();

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = useLocation().pathname;

  const themeId = useRef(currentTheme.id);
  const isRightToLeft = useRef(appearance.isRightToLeft);
  const sideBarType = useRef(null);
  const unreadNotificationsCount = useSelector(
    (state) => state.notificationsCounter
  );

  const { t } = useTranslation();

  let unreadNotificationsCountText =
    unreadNotificationsCount > 99 ? "99+" : unreadNotificationsCount;

  //inject live chat
  useInjectLiveChat(currentUser.IsAuthenticated);

  //job to update unread not. count every 5 minutes
  const updateUnreadNotificationsCount = async () => {
    try {
      var response = await api({
        url: "/notification/getunreadnotificationscount",
        method: "post",
        //data: {...entity },
        //headers:{'h':'1'}
      });

      let data = response.data.Data;
      dispatch(setCounter(data.Count));
    } catch (err) {}
  };

  useEffectOnce(() => {
    let timerId = window.setInterval(() => {
      if (_currentUser.current.IsAuthenticated)
        updateUnreadNotificationsCount();
    }, NOTIFICATIONS_UPDATE_COUNT_IN_MILLISECONDS);

    return () => {
      window.clearInterval(timerId);
    };
  }, []);

  sideBarType.current = currentUser.SideBarType;

  const handleOnLogin = (e) => {
    navigate("/login", { replace: true, state: { from: { ...location } } });
  };

  const handleOnThemeSave = (e) => { 
    let _themeId = themeId.current;
    let _isRightToLeft = appearance.isRightToLeft;
    let _sideBarType = sideBarType.current;
    let langId = currentUser.LanguageId;

    if (currentTheme.processing) return;

    dispatch(
      setUserTheme(_themeId, _isRightToLeft, _sideBarType, langId, t)
    ).then(
      (response) => {
        dispatch(setAppearance({ isRightToLeft: _isRightToLeft }));

        dispatch(setCurrentUser({ ...currentUser, SideBarType: _sideBarType }));
        dispatch(setCurrentTheme(themes[_themeId.toString()]));
        switcher({ theme: themes[_themeId.toString()] });

        if (_sideBarType == 1) dispatch(setSideBarVisible(false));
        else dispatch(setSideBarVisible(true));

        dispatch(setSideBarDetailVisible(false));

        dispatch(hideAppDialog());
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("currentuser.unknown_error")];
          else errorMessages = getErrorMessages(errorMessages, 1);

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("currentuser.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            })
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      }
    );
  };

  const handleItemClick = (e) => {
    let itemId = e.item.id;

    if (itemId == "theming") {
      dispatch(
        setAppDialog({
          isVisible: true,
          closable: true,
          title: t("currentuser.appearance"),
          className: "themes-selector",
          content: (
            <ThemesSelector
              onThemeChange={(e) => {
                themeId.current = e.value;
              }}
              onIsRightToLeftChange={(e) => {
                isRightToLeft.current = e;
              }}
              onSideBarChange={(e) => {
                sideBarType.current = e.value;
              }}
            />
          ),
          actions: {
            yesButton: {
              title: t("currentuser.apply"),
              handler: (e) => handleOnThemeSave(e),
            },
            noButton: {
              title: t("currentuser.close"),
            },
          },
        })
      );
    } else if (itemId == "changePassword") setChangePasswordOpen(true);
    else if (itemId == "reportsMenu")
      navigate("/sidebardesigner", { replace: true });
    else if (itemId == "vasai") {
      beepFile("/sounds/start.mp3");
      dispatch(toggleService());
    } else if (itemId == "consumption")
      navigate("/dataconsumption", { replace: true });
    else if (itemId == "users") navigate("/users", { replace: true });
    else if (itemId == "groups") navigate("/securitygroups", { replace: true });
    else if (itemId == "logOut") {
      dispatch(logout({})).then((action) => {
        let payload = action.payload;
        if (payload.succeeded == false) {
          dispatch(
            addNotification({
              text: t("currentuser.failed"),
              closable: true,
              duration: notificationDurationMiddle,
              type: "error",
            })
          );
        } else {
          navigate("/", { replace: true });
          //navigate(0);
        }
      });
    } else if (itemId == "support")
      window.open(
        "https://help.cerebro360.ai/helpdesk/cerebro-help-2",
        "_blank"
      );
    else if (itemId == "schedule") navigate("/schedule", { replace: true });
     else if (itemId == "connection") navigate("/connection", { replace: true });
  };

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  return (
    <React.Fragment>
      {currentUser.IsAuthenticated && (
        <div className="buttons-container">
          <ul className="main">
            <li>
              <div className="link">
                <div className="item">
                  <NavLink to={"/notifications"}>
                    <SvgIcon
                      icon={bellIcon}
                      color={currentTheme.particlesColor}
                    />
                    {unreadNotificationsCount > 0 && (
                      <Badge
                        shape="rounded"
                        themeColor="error"
                        style={{
                          color: "#fff",
                          padding: 3,
                          right: "unset",
                          top: "unset",
                          marginLeft: "-15px",
                          backgroundColor: "red",
                        }}
                      >
                        {unreadNotificationsCountText}
                      </Badge>
                    )}
                  </NavLink>
                </div>
              </div>
            </li>
            <li>
              <div className="link">
                <div className="item">
                  <NavLink
                    to={"https://cerebro360.ai/ic360doc"}
                    target="_blank"
                  >
                    <SvgIcon
                      icon={documentManagerIcon}
                      color={currentTheme.particlesColor}
                      style={{ margin: "-2px 0 0 0 " }}
                    />
                  </NavLink>
                </div>
              </div>
            </li>
          </ul>
          {(canAccessUsers(currentUser) ||
            currentUser.Extras?.ViewConsumption) && (
            <DropDownButton
              shape="rectangle"
              size="medium"
              fillMode="flat"
              rounded="medium"
              icon="wrench"
              itemRender={itemRender}
              items={getUserManagementItems(currentUser, dictaphone, t)}
              onItemClick={handleItemClick}
              className="current-user"
              popupSettings={{ popupClass: "current-user-popup" }}
            />
          )}
          <DropDownButton
            shape="rectangle"
            size="medium"
            fillMode="flat"
            rounded="medium"
            icon="gear"
            itemRender={itemRender}
            items={getUserItems(currentUser, currentTheme, dictaphone, t)}
            onItemClick={handleItemClick}
            className="current-user"
            popupSettings={{ popupClass: "current-user-popup" }}
          />
          <div
            style={{
              display: "flex",
              textAlign: "right",
              overflow: "hidden",
              alignItems: "center",
            }}
          >
            <NavLink
              to={"/accountmanagement"}
              style={{
                display: "flex",
                flexDirection: "row",
              }}
            >
              <div
                style={{
                  display: "flex",
                  margin: "0 10px",
                  flexDirection: "column",
                }}
              >
                <div className="current-user-email">{currentUser.Email}</div>
                <div
                  className="current-subscription-name"
                  style={{
                    textTransform: "uppercase",
                  }}
                >
                  {currentUser.FullName}
                </div>
              </div>
              <div className="user-avatar">
                <SvgIcon icon={userIcon} color={currentTheme.particlesColor} />
              </div>
            </NavLink>
          </div>

          <style>
            {`
                                .k-popup ul{
                                    padding: 0px;
                                }
                                
                                .k-popup ul > li{
                                    margin: 0                                    
                                }
                                
                                .k-popup ul > li > span{
                                    border: none !important;
                                    box-shadow: none !important;
                                }
                                .k-menu-item
                                {
                                  padding: 0;
                                }
                                .k-menu-link{
                                    padding: 0 2px !important;
                                }
                                     .current-user{
                                     ${
                                       appearance.isRightToLeft
                                         ? "margin-left:16px"
                                         : "margin-right:16px"
                                     }
                                     }

                                .current-user .k-icon{
                                  color:${currentTheme.particlesColor};
                                }

                                .current-user:hover{
                                  color:${currentTheme.componentText};
                                }

                                .current-user-popup .k-menu-item:nth-child(2),
                                .current-user-popup .k-menu-item:nth-child(4),
                                .current-user-popup .k-menu-item:nth-child(6)
                                {
                                  padding: 0 !important;
                                }

                                .current-user-popup .k-menu-item:nth-child(1),
                                .current-user-popup .k-menu-item:nth-child(3),
                                .current-user-popup .k-menu-item:nth-child(5),
                                .current-user-popup .k-menu-item:nth-child(7)
                                {
                                  padding-top: 7px;
                                  padding-bottom: 7px;
                                }

                                .current-user-popup .sub-menu-link{
                                width:100%;
                                  padding: 2px 10px;
                                  display:flex;
                                  flex-direction:${
                                    appearance.isRightToLeft
                                      ? "row-reverse"
                                      : "row"
                                  };
                                }

                                .current-user-popup .sub-menu-link .k-icon{
                                    ${
                                      appearance.isRightToLeft
                                        ? "margin-left:12px"
                                        : "margin-right:12px"
                                    }
                                }

                                .user-avatar{
                                    width: 25px;
                                    height: 25px;
                                    border: 1px solid #fff;
                                    display: flex;
                                    align-items: center;
                                    align-content: center;
                                    border-radius: 50%;
                                    padding: 4px;
                                }
                            `}
          </style>
        </div>
      )}
      {
        //draw the login button if not authenticated
        !currentUser.IsAuthenticated &&
          !currentPath.startsWith("/subscription") && (
            <div className="buttons-container">
              <Button className="login-button" onClick={handleOnLogin}>
                {t("login.login")}
              </Button>
              <style>
                {` 
                      .login-button
                      {
                        padding: 6px 24px;
                        /*border: 2px solid ${currentTheme.touchColor};*/
                        border: none;
                        background-color: transparent;
                        background-image: none;
                        color: ${currentTheme.particlesColor};
                        font-weight: 600;
                        font-size: 14px;
                      }

                      .login-button:hover
                      {
                        cursor: pointer;
                        background-color: transparent;
                        color: ${currentTheme.dialogToolbarHoverText};
                      }

                      `}
              </style>
            </div>
          )
      }

      {!currentUser?.IsAuthenticated &&
        currentPath.startsWith("/subscription") && (
          <div className="buttons-container">
            <ul className="main">
              <li>
                <div className="link">
                  <div className="item">
                    <NavLink
                      onClick={(e) => {
                        if (currentPath.startsWith("/subscription")) {
                          e.preventDefault(); // block navigation
                          e.stopPropagation(); // block sub-menu logic
                        }
                      }}
                      style={{
                        pointerEvents: currentPath.startsWith("/subscription")
                          ? "none"
                          : "auto",
                        cursor: currentPath.startsWith("/subscription")
                          ? "default"
                          : "pointer",
                      }}
                    >
                      <SvgIcon
                        icon={bellIcon}
                        color={currentTheme.particlesColor}
                      />
                      {unreadNotificationsCount > 0 && (
                        <Badge
                          shape="rounded"
                          themeColor="error"
                          style={{
                            color: "#fff",
                            padding: 3,
                            right: "unset",
                            top: "unset",
                            marginLeft: "-15px",
                            backgroundColor: "red",
                          }}
                        >
                          {unreadNotificationsCountText}
                        </Badge>
                      )}
                    </NavLink>
                  </div>
                </div>
              </li>
              <li>
                <div className="link">
                  <div className="item">
                    <NavLink
                      to={"https://cerebro360.ai/ic360doc"}
                      target="_blank"
                    >
                      <SvgIcon
                        icon={documentManagerIcon}
                        color={currentTheme.particlesColor}
                        style={{ margin: "-2px 0 0 0 " }}
                      />
                    </NavLink>
                  </div>
                </div>
              </li>
            </ul>

            <div
              style={{
                display: "flex",
                textAlign: "right",
                overflow: "hidden",
                alignItems: "center",
              }}
            >
              <NavLink
                onClick={(e) => {
                  if (currentPath.startsWith("/subscription")) {
                    e.preventDefault(); // block navigation
                    e.stopPropagation(); // block sub-menu logic
                  }
                }}
                style={{
                  pointerEvents: currentPath.startsWith("/subscription")
                    ? "none"
                    : "auto",
                  cursor: currentPath.startsWith("/subscription")
                    ? "default"
                    : "pointer",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    margin: "0 10px",
                    flexDirection: "column",
                  }}
                >
                  <div className="current-user-email">
                    {onBoarding?.pendingUser?.Email}
                  </div>
                  <div
                    className="current-subscription-name"
                    style={{
                      textTransform: "uppercase",
                    }}
                  >
                    {onBoarding?.pendingUser?.Username}
                  </div>
                </div>
                <div className="user-avatar">
                  <SvgIcon
                    icon={userIcon}
                    color={currentTheme.particlesColor}
                  />
                </div>
              </NavLink>
            </div>

            <style>
              {`
                                .k-popup ul{
                                    padding: 0px;
                                }
                                
                                .k-popup ul > li{
                                    margin: 0                                    
                                }
                                
                                .k-popup ul > li > span{
                                    border: none !important;
                                    box-shadow: none !important;
                                }
                                .k-menu-item
                                {
                                  padding: 0;
                                }
                                .k-menu-link{
                                    padding: 0 2px !important;
                                }

                                .current-user .k-icon{
                                  color:${currentTheme.particlesColor};
                                }

                                .current-user:hover{
                                  color:${currentTheme.componentText};
                                }

                                .current-user-popup .k-menu-item:nth-child(2),
                                .current-user-popup .k-menu-item:nth-child(4),
                                .current-user-popup .k-menu-item:nth-child(6)
                                {
                                  padding: 0 !important;
                                }

                                .current-user-popup .k-menu-item:nth-child(1),
                                .current-user-popup .k-menu-item:nth-child(3),
                                .current-user-popup .k-menu-item:nth-child(5),
                                .current-user-popup .k-menu-item:nth-child(7)
                                {
                                  padding-top: 7px;
                                  padding-bottom: 7px;
                                }

                                .user-avatar{
                                    width: 25px;
                                    height: 25px;
                                    border: 1px solid #fff;
                                    display: flex;
                                    align-items: center;
                                    align-content: center;
                                    border-radius: 50%;
                                    padding: 4px;
                                }
                            `}
            </style>
          </div>
        )}
      {changePasswordOpen && (
        <Dialog className="change-password-form-dialog" title={null}>
          <ChangePasswordForm
            currentTheme={currentTheme}
            onCancel={(e) => setChangePasswordOpen(false)}
          />
        </Dialog>
      )}
      <style>
        {`
                    .change-password-form-dialog .k-dialog-content
                    {
                      padding: 0 !important;
                      width: 400px;

                    }
                `}
      </style>
    </React.Fragment>
  );
};
